import { AddButtonIcon } from '@/assets/static/icons/add-button-icon';
import Button from '@/components/basicComponents/button';
import { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import Tabs, { ITab } from '@/components/basicComponents/tabs';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { ALL_ADVISORY_FIRM_LIST_QUERY } from '@/components/investors-page/queries';
import { ITenant } from '@/components/investors-page/types';
import { initialValuesFromTable } from '@/components/table/constants';
import FilterPage from '@/components/table/filterPage';
import TablePagination from '@/components/table/tablePagination';
import { IItems, ISort, SORT_ORDER } from '@/components/table/types';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useMutation, useQuery } from '@apollo/client';
import { ROLE } from '@trace-8/trace8-schema';
import { isValid as isValidDate } from 'date-fns';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import FirmTable from './firmTable/table';
import AddNewUser from './modals/addNewUser';
import ChangeEmail from './modals/changeEmail';
import DeleteUser from './modals/deleteUser';
import Impersonate from './modals/impersonate';
import MakePrimaryAdmin from './modals/makePrimaryAdmin';
import SendResetPasswordEmailDialog from './modals/sendResetPasswordEmail';
import SendWelcomeEmail from './modals/sendWelcomeEmail';
import { ModifyUserModalPage } from './modifyUserModalPage';
import { LIST_ADVISORY_FIRM_USERS_QUERY, LIST_PLATFORM_MANAGER_USERS_QUERY, TOGGLE_USER_STATUS_MUTATION } from './queries';
import TenantsTable from './tenantsTable/table';
import { IUser } from './types';

interface UsersProps {
  backToTitle?: string;
  handleClose: () => void;
}

export const usersPageTabs: ITab[] = [
  { value: 'Your Firm', amount: null },
  { value: 'Other Tenants', amount: null }
];

export const Users = ({ backToTitle, handleClose }: UsersProps) => {
  const navigate = useNavigate();
  const { isMobile, isTablet } = useResponsive();
  const { user, signInAs } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get('tab') || usersPageTabs[0].value;
  const action = searchParams.get('action') || '';
  const q = searchParams.get('q') || '';
  const key = searchParams.get('key') || 'email';
  const asc = searchParams.get('asc') === null || searchParams.get('asc') === 'true';
  const limit = searchParams.get('limit') || initialValuesFromTable.initialAmountRowsPerPage[0];
  const offset = searchParams.get('offset') || 0;
  const advisoryFirmQuickFilter = searchParams.get('advisoryFirmQuickFilter') || '';

  const [advisoryFirmQuickFilterOptions, setAdvisoryFirmQuickFilterOptions] = useState<ISelectValue[]>([]);
  const [firmUsers, setFirmUsers] = useState<IUser[]>([]);
  const [otherTenantUsers, setOtherTenantUsers] = useState<IUser[]>([]);
  const [advisoryFirmTenants, setAdvisoryFirmTenants] = useState<ITenant[]>([]);
  const [isModifyUserModalPageOpen, setIsModifyUserModalPageOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<IUser | null>(null);
  const [tenantId, setTenantId] = useState<string>('');
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: 'impersonate' });
  const [impersonateUser, setImpersonateUser] = useState<IUser | null>(null);
  const [impersonationLoading, setImpersonationLoading] = useState(false);
  const [flashUpdatingRow, setFlashUpdatingRow] = useState<string>(null);

  const [toggleUserStatus] = useMutation(TOGGLE_USER_STATUS_MUTATION);

  useQuery<{ allAdvisoryFirmList: ITenant[] }>(ALL_ADVISORY_FIRM_LIST_QUERY, {
    variables: {
      data: {
        sortBy: 'NAME',
        sortDirection: SORT_ORDER.ASC
      }
    },
    onCompleted: ({ allAdvisoryFirmList }) => {
      setAdvisoryFirmTenants(allAdvisoryFirmList);
      setAdvisoryFirmQuickFilterOptions([
        createSimpleSelectOption('', 'All'),
        ...new Set(allAdvisoryFirmList.map((el) => createSimpleSelectOption(el.id, el.name)))
      ]);
    }
  });

  const {
    data: listPlatformManagerUsersData,
    refetch: listPlatformManagerUsersRefetch,
    loading: listPlatformManagerUsersLoading
  } = useQuery<{
    listPlatformManagerUsers: { total: number; page: number; perPage: number; data: IUser[] };
  }>(LIST_PLATFORM_MANAGER_USERS_QUERY, {
    skip: !(activeTab === usersPageTabs[0].value),
    variables: {
      filters: {
        search: q,
        sortBy: key,
        sortDirection: asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
        limit: Number(limit),
        page: Math.floor(Number(offset) / Number(limit)) + 1
      }
    },
    onCompleted: ({ listPlatformManagerUsers }) => {
      setFirmUsers(listPlatformManagerUsers.data);
    }
  });

  const {
    data: listAdvisoryFirmUsersData,
    refetch: listAdvisoryFirmUsersRefetch,
    loading: listAdvisoryFirmUsersLoading
  } = useQuery<{
    listAdvisoryFirmUsers: { total: number; page: number; perPage: number; data: IUser[] };
  }>(LIST_ADVISORY_FIRM_USERS_QUERY, {
    skip: !(activeTab === usersPageTabs[1].value),
    variables: {
      filters: {
        tenantId: advisoryFirmQuickFilter,
        search: q,
        sortBy: key,
        sortDirection: asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
        limit: Number(limit),
        page: Math.floor(Number(offset) / Number(limit)) + 1
      }
    },
    onCompleted: ({ listAdvisoryFirmUsers }) => {
      setOtherTenantUsers(listAdvisoryFirmUsers.data);
    }
  });

  const openImpersonateModalWindow = (user: IUser, type: string) => {
    setImpersonateUser(user);
    setImpersonationLoading(false);
    setModalWindow({ type, isOpen: true });
  };

  const closeImpersonateModalWindow = () => {
    setModalWindow({ ...modalWindow, isOpen: false });
    setImpersonateUser(null);
  };

  const openModalWindow = (type: string, user?: IUser) => {
    setModalWindow({ type, isOpen: true });
    setCurrentUser(user || null);
  };

  const closeModalWindow = () => {
    setModalWindow({ ...modalWindow, isOpen: false });
    setCurrentUser(null);
  };

  const impersonate = () => {
    if (!impersonateUser.email) {
      closeImpersonateModalWindow();
      return;
    }

    setImpersonationLoading(true);
    signInAs(impersonateUser.email)
      .then(() => {
        navigate('/dealCatalog');
      })
      .catch(console.error);
  };

  const handleAddNewUser = () => {
    if (activeTab === usersPageTabs[0].value) {
      handleOpenModifyUserModalPage({ open: true, tenantId: user?.tenant?.id ?? '' });
      return;
    }
    if (activeTab === usersPageTabs[1].value) {
      openModalWindow('add-new-user');
      return;
    }
  };

  const handleOpenModifyUserModalPage = ({ open, currentUser, tenantId }: { open: boolean; currentUser?: IUser; tenantId?: string }) => {
    setIsModifyUserModalPageOpen(open);
    setCurrentUser(currentUser || null);
    setTenantId(tenantId || '');
  };

  const handleFlashUpdatingRow = (id: string | null) => {
    setFlashUpdatingRow(id);
  };

  const handleToggleUserStatus = async (user: IUser) => {
    if (!user) return;

    try {
      handleFlashUpdatingRow(user.id);
      await toggleUserStatus({
        variables: {
          userId: user.id
        }
      });

      await getCurrentStatus().refetch();
    } catch (error) {
      console.error(error);
    } finally {
      handleFlashUpdatingRow(null);
    }
  };

  const actionsByStatus = (user: IUser): IItems[] => {
    return [
      ...(activeTab === usersPageTabs[1].value && user.lastLogin !== 'Disabled'
        ? [
            {
              name: 'Impersonate',
              onClick: () => openImpersonateModalWindow(user, 'impersonate')
            }
          ]
        : []),
      {
        name: 'Edit',
        onClick: () => handleOpenModifyUserModalPage({ open: true, currentUser: user, tenantId: user?.tenant?.id ?? '' })
      },
      ...(user.lastLogin === 'Never'
        ? [
            {
              name: 'Send Welcome Email',
              onClick: () => openModalWindow('send-welcome-email', user)
            }
          ]
        : []),
      ...(user.lastLogin && isValidDate(new Date(user.lastLogin))
        ? [
            {
              name: 'Reset Password',
              onClick: () => openModalWindow('send-reset-password-email', user)
            }
          ]
        : []),
      ...(!user.roles.includes(getEnumKeyByValue(ROLE, ROLE.PRIMARY_ADMIN))
        ? [
            {
              name: 'Make Primary Admin',
              onClick: () => openModalWindow('make-primary-admin', user)
            }
          ]
        : []),

      ...(user.lastLogin && isValidDate(new Date(user.lastLogin))
        ? [
            {
              name: 'Change Email',
              onClick: () => openModalWindow('change-email', user)
            }
          ]
        : []),
      ...(!!user.lastLogin && user.lastLogin !== 'Disabled'
        ? [
            {
              name: 'Disable',
              onClick: () => handleToggleUserStatus(user)
            }
          ]
        : []),
      ...(user.lastLogin === 'Disabled'
        ? [
            {
              name: 'Reactivate',
              onClick: () => handleToggleUserStatus(user)
            }
          ]
        : []),
      ...(user.lastLogin === 'Never' && !user.isFinalUser
        ? [
            {
              name: 'Delete',
              onClick: () => openModalWindow('delete-user', user)
            }
          ]
        : [])
    ];
  };

  const getCurrentStatus = () => {
    switch (activeTab) {
      case usersPageTabs[0].value:
        return {
          refetch: listPlatformManagerUsersRefetch,
          total: listPlatformManagerUsersData?.listPlatformManagerUsers?.total ?? 0
        };
      case usersPageTabs[1].value:
        return {
          refetch: listAdvisoryFirmUsersRefetch,
          total: listAdvisoryFirmUsersData?.listAdvisoryFirmUsers?.total ?? 0
        };
      default:
        return {
          refetch: () => null as any,
          total: 0
        };
    }
  };

  const onChangeSearch = (searchValue: string) => {
    setSearchParams(
      (prev) => {
        if (!searchValue) {
          prev.delete('q');
          return prev;
        }
        prev.set('q', searchValue);
        prev.delete('limit');
        prev.delete('offset');
        return prev;
      },
      { replace: true }
    );
  };

  const onChangePaginationData = (limit: number, offset: number) => {
    setSearchParams(
      (prev) => {
        prev.set('limit', limit.toString());
        prev.set('offset', offset.toString());
        return prev;
      },
      { replace: true }
    );
  };

  const onChangeSort = (value: ISort) => {
    setSearchParams(
      (prev) => {
        prev.set('key', value.key);
        prev.set('asc', value.asc.toString());
        return prev;
      },
      { replace: true }
    );
  };

  const onChangeAdvisoryFirmQuickFilter = (advisoryFirmId: string) => {
    setSearchParams(
      (prev) => {
        advisoryFirmId ? prev.set('advisoryFirmQuickFilter', advisoryFirmId) : prev.delete('advisoryFirmQuickFilter');
        prev.delete('limit');
        prev.delete('offset');
        return prev;
      },
      { replace: true }
    );
  };

  const onChangeActiveTab = (value: string) => {
    setSearchParams({ action, tab: value }, { replace: true });
  };

  if (isModifyUserModalPageOpen) {
    return (
      <ModifyUserModalPage
        tenantId={tenantId}
        user={currentUser}
        activeTab={activeTab}
        backToTitle="Users"
        openModalWindow={openModalWindow}
        refetch={getCurrentStatus().refetch}
        handleClose={() => handleOpenModifyUserModalPage({ open: false })}
      />
    );
  }

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'impersonate' && (
        <Impersonate
          isOpen={modalWindow.isOpen}
          onConfirm={impersonate}
          onCancel={closeImpersonateModalWindow}
          user={impersonateUser}
          signingIn={impersonationLoading}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'add-new-user' && (
        <AddNewUser
          isOpen={modalWindow.isOpen}
          advisoryFirmList={advisoryFirmTenants}
          handleOpenModifyUserModalPage={handleOpenModifyUserModalPage}
          onClose={closeModalWindow}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'change-email' && (
        <ChangeEmail isOpen={modalWindow.isOpen} currentUser={currentUser} onClose={closeModalWindow} />
      )}
      {modalWindow.isOpen && modalWindow.type === 'delete-user' && (
        <DeleteUser
          isOpen={modalWindow.isOpen}
          activeTab={activeTab}
          currentUser={currentUser}
          refetch={getCurrentStatus().refetch}
          onClose={closeModalWindow}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'send-reset-password-email' && (
        <SendResetPasswordEmailDialog isOpen={modalWindow.isOpen} currentUser={currentUser} onClose={closeModalWindow} />
      )}
      {modalWindow.isOpen && modalWindow.type === 'send-welcome-email' && (
        <SendWelcomeEmail isOpen={modalWindow.isOpen} currentUser={currentUser} onClose={closeModalWindow} />
      )}
      {modalWindow.isOpen && modalWindow.type === 'make-primary-admin' && (
        <MakePrimaryAdmin
          isOpen={modalWindow.isOpen}
          activeTab={activeTab}
          currentUser={currentUser}
          handleFlashUpdatingRow={handleFlashUpdatingRow}
          refetch={getCurrentStatus().refetch}
          onClose={closeModalWindow}
        />
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleClose} backToTitle={backToTitle} />} />
        <PageTitle title="Users" />
      </MainWrap>
      <MainWrap>
        <PaddingWrap>
          <Tabs tabs={usersPageTabs} activeTab={activeTab} setActiveTab={onChangeActiveTab} />
          <TabsLine />
          <AddButtonWrapper>
            <Button size="md" onClick={handleAddNewUser} icon={<AddButtonIcon />}>
              Add New
            </Button>
          </AddButtonWrapper>
          <FilterPage
            search={{
              value: q,
              onChange: onChangeSearch,
              placeholder: 'Search'
            }}
            {...(activeTab === usersPageTabs[1].value && {
              advisoryFirmQuickFilter: {
                title: 'Advisory Firm',
                value: advisoryFirmQuickFilter,
                defaultValue: 'All',
                options: advisoryFirmQuickFilterOptions,
                onChange: onChangeAdvisoryFirmQuickFilter,
                centerList: true
              }
            })}
            resultsValue={getCurrentStatus().total}
            refetch={getCurrentStatus().refetch}
            useNewFilters
          />
          <TableWrapper>
            {activeTab === usersPageTabs[0].value && (
              <FirmTable
                firmTableTableData={firmUsers}
                sort={{ key, asc }}
                loading={listPlatformManagerUsersLoading}
                flashUpdatingRow={flashUpdatingRow}
                setSort={onChangeSort}
                actionsByStatus={actionsByStatus}
              />
            )}
            {activeTab === usersPageTabs[1].value && (
              <TenantsTable
                tenantsTableTableData={otherTenantUsers}
                sort={{ key, asc }}
                loading={listAdvisoryFirmUsersLoading}
                flashUpdatingRow={flashUpdatingRow}
                setSort={onChangeSort}
                actionsByStatus={actionsByStatus}
              />
            )}
            <PaginationWrap>
              <TablePagination
                savePagination={onChangePaginationData}
                paginationValues={{
                  limit: Number(limit),
                  offset: Number(offset),
                  total: getCurrentStatus().total
                }}
                refetch={() => {}}
              />
            </PaginationWrap>
          </TableWrapper>
          <FooterModalWrap isMobile={isMobile} isTablet={isTablet} />
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

const PaddingWrap = styled.div`
  padding-left: 16px;
  padding-right: 16px;
  @media (min-width: 600px) {
    padding-left: 50px;
    padding-right: 50px;
  }
`;

const TabsLine = styled.div`
  border-bottom: 1px ${themeVar('border.base')} solid;
  margin: 0 -16px;
  @media (min-width: 600px) {
    margin: 0 -50px;
  }
`;

const AddButtonWrapper = styled.div`
  margin-top: 40px;
`;

const TableWrapper = styled.div`
  margin-bottom: 110px;
`;

const PaginationWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

const FooterModalWrap = styled.div<{ isMobile: boolean; isTablet: boolean }>`
  width: ${({ isMobile, isTablet }) => (isMobile || isTablet ? '100%' : 'calc(100% - 80px)')};
  display: flex;
  align-items: center;
  justify-content: center;
  height: 90px;
  padding: 10px 80px;
  position: fixed;
  left: ${({ isMobile, isTablet }) => (isMobile || isTablet ? '0' : '80px')};
  bottom: 0;
  clear: both;
  background-color: ${themeVar('layer.1')};
  border-top: 1px solid ${themeVar('border.base')};
  z-index: 9;
`;
